// @flow
import orderBy from 'lodash/orderBy';
import flatten from 'lodash/flatten';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import type { ID_TYPE } from '../../../types/common';
import type { ROUTE_STOP_TYPE } from '../../../types/routes';
import * as selectors from '../../../reducers';
import * as studentActions from '../../../actions/students';
import RouteStopWeekSchedule from './RouteStopWeekSchedule';
import GatewayWeekSchedule from './GatewayWeekSchedule';
import StudentStopAssignationCard from '../StudentStopAssignationCard';
import GatewayAssignationCard from '../GatewayAssignationCard';
import DummyWeekDivision from '../DummyWeekDivision';


type RoutesSchedulerPropTypes = {
  value?: {[ID_TYPE]: {
    week_days: Array<number>,
  }},
  onChange?: Function,
  stops?: Array<ROUTE_STOP_TYPE>,
  gateways?: Array<{id: string, week_days: Array<number>, gatewayId: number}>,
  onRemove?: Function,
  title: string,
};

const RoutesScheduler = ({
  value = {},
  onChange,
  onRemove,
  stops = [],
  gateways = [],
  title = '',
}: RoutesSchedulerPropTypes) => (
  <Fragment>
    {(stops.length > 0 || gateways.length > 0) && <DummyWeekDivision title={title} />}
    {
      stops.map(({ id }) => {
        const occupiedDays = new Set(
          flatten(
            Object.keys(value)
              .filter((lId) => {
                // Cast to number key
                if (typeof id === 'number') {
                  return parseInt(lId, 10) !== id;
                }

                return lId !== id;
              })
              .map(lId => value[lId].week_days),
          ),
        );

        return (
          <tr key={id}>
            <StudentStopAssignationCard
              onRemove={() => {
                if (onRemove) {
                  onRemove(id);
                }

                if (onChange) {
                  const newValue = { ...value };
                  delete newValue[id];
                  onChange(newValue);
                }
              }}
              stopId={id}
              occupiedDays={occupiedDays}
            />
            <RouteStopWeekSchedule
              stopId={id}
              value={value[id] ? value[id].week_days : undefined}
              occupiedDays={occupiedDays}
              onChange={(weekSchedule, added, day) => {
                if (onChange) {
                  const newValue = { ...value };
                  if (added) {
                    Object.keys(value).filter(stopId => stopId !== id).forEach(
                      (stopId) => {
                        if (value[stopId].week_days.includes(day)) {
                          newValue[stopId] = {
                            ...newValue[stopId],
                            week_days: value[stopId].week_days.filter(lDay => lDay !== day),
                          };
                        } else {
                          newValue[stopId] = value[stopId];
                        }
                      },
                    );
                  }
                  newValue[id].week_days = weekSchedule;

                  onChange(newValue);
                }
              }}
            />
          </tr>
        );
      })
    }
    {
      gateways.map(({ id, gatewayId }) => {
        const occupiedDays = new Set(
          flatten(
            Object.keys(value)
              .filter((lId) => {
                // Cast to number key
                if (typeof id === 'number') {
                  return parseInt(lId, 10) !== id;
                }

                return lId !== id;
              })
              .map(lId => value[lId].week_days),
          ),
        );
        if (value && value[id]) {
          return (
            <tr key={id}>
              <GatewayAssignationCard
                onRemove={() => {
                  if (onRemove) {
                    onRemove(id);
                  }
                  if (onChange) {
                    const newValue = { ...value };
                    delete newValue[id];
                    onChange(newValue);
                  }
                }}
                gatewayId={gatewayId}
                occupiedDays={occupiedDays}
              />
              <GatewayWeekSchedule
                value={value[id].week_days}
                occupiedDays={occupiedDays}
                onChange={(weekSchedule, added, day) => {
                  if (onChange) {
                    const newValue = { ...value };
                    if (added) {
                      Object.keys(value).filter(elmId => elmId !== id).forEach(
                        (elmId) => {
                          if (value[elmId].week_days.includes(day)) {
                            newValue[elmId] = {
                              ...newValue[elmId],
                              week_days: value[elmId].week_days.filter(lDay => lDay !== day),
                            };
                          } else {
                            newValue[elmId] = value[elmId];
                          }
                        },
                      );
                    }

                    newValue[id].week_days = weekSchedule;

                    onChange(newValue);
                  }
                }}
              />
            </tr>
          );
        }
        return null;
      })
    }
  </Fragment>
);


export default connect(
  (state, { studentId, routeType }) => {
    const { weekRouteStopSchedule } = selectors.getStudent(state, studentId);
    let stops = [];
    const compareAction = routeType === 'going' ? 'entry' : 'exit';
    const gatewayUserActions = selectors.getStudentGatewayUserActions(state,
      studentId).filter(elm => elm.action === compareAction);
    const gatewaysObjs = gatewayUserActions.map(elm => ({
      ...elm.gateway,
      id: elm.action === 'entry' ? `gg_${elm.gateway.id}` : `gr_${elm.gateway.id}`,
      gatewayId: elm.gateway.id,
    }));

    // If already has schedule
    if (weekRouteStopSchedule) {
      stops = orderBy(
        Object.keys(weekRouteStopSchedule)
          .map(id => selectors.getRouteStop(state, id))
          .filter(stop => selectors.getRoute(state, stop.route).route_type === routeType),
        ['name'],
      );
    }

    return {
      stops,
      gateways: gatewaysObjs,
      weekRouteStopSchedule,
    };
  },
  (dispatch, { studentId }) => ({
    onRemove(stopId, weekRouteStopSchedule) {
      const newSchedule = { ...weekRouteStopSchedule };
      delete newSchedule[stopId];
      dispatch(studentActions.updateStudentSuccess({
        id: studentId,
        weekRouteStopSchedule: newSchedule,
      }));
    },
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onRemove(stopId) {
      dispatchProps.onRemove(stopId, stateProps.weekRouteStopSchedule);
    },
  }),
)(RoutesScheduler);



// WEBPACK FOOTER //
// ./src/scripts/components/StudentSchedulingPopup/RouteStopsScheduler/index.jsx